import { z } from "zod";
import { ATTACHMENT_ALLOWED_MIME_TYPES, ATTACHMENT_MAX_COUNT } from "./attachments.js";
import { AgentSkillCatalogEntrySchema } from "./domain.js";
import { LibraryFilterSchema } from "./library.js";
import { TicketReferenceSchema, WORKSPACE_APPS } from "./workspace.js";

export type WorkspaceAppId =
  | (typeof WORKSPACE_APPS)[number]["id"]
  | "home"
  | "shared-bots"
  | "apps"
  | "notifications";

/** Only app-owned routes are restorable. Never persist computer session URLs or auth queries. */
export function workspaceDestination(path: string, platform: "web" | "mobile" = "web") {
  if (!path.startsWith("/") || path.startsWith("//") || path.includes("\\")) return null;
  const url = new URL(path, "https://workspace.invalid");
  const pathname = url.pathname.replace(/\/$/, "") || "/";
  const home = platform === "web" ? "/app" : "/";
  if (pathname === home) return { id: "home" as WorkspaceAppId, name: "Home", path: home };
  if (platform === "web" && pathname === "/app/board")
    return { id: "projects" as WorkspaceAppId, name: "Projects", path: "/app/projects" };
  const prefix = platform === "web" ? "/app" : "";
  const app = WORKSPACE_APPS.find((item) => pathname === item[platform]);
  let id: WorkspaceAppId | undefined = app?.id;
  let name: string | undefined = app?.name;
  if (platform === "web" && pathname === "/app/notifications") {
    id = "notifications";
    name = "Notifications";
  }
  if (pathname === `${prefix}/workspace/apps`) {
    id = "apps";
    name = "Apps";
  }
  if (
    platform === "mobile" &&
    ["/thread", "/group-thread", "/peer-messages", "/routine"].includes(pathname)
  ) {
    id = "messages";
    name = "Messages";
  }
  if (platform === "mobile" && pathname === "/human-computer") {
    id = "computers";
    name = "Atlantis";
  }
  if (platform === "mobile" && pathname === "/project-ticket") {
    id = "projects";
    name = "Projects";
  }
  if (platform === "mobile" && pathname === "/artifact") {
    id = "files";
    name = "Files";
  }
  if (pathname === `${prefix}/shared-bots`) {
    id = "shared-bots";
    name = "Shared bots";
  }
  if (new RegExp(`^${prefix}/artifacts/[^/]+$`).test(pathname)) {
    id = "files";
    name = "Files";
  }
  if (
    new RegExp(`^${prefix}/g/[^/]+$`).test(pathname) ||
    (platform === "web" &&
      /^\/app\/[^/]+$/.test(pathname) &&
      !id &&
      !["/app/board", "/app/mission-preview"].includes(pathname)) ||
    (platform === "mobile" && /^\/bot\/[^/]+$/.test(pathname))
  ) {
    id = "messages";
    name = "Messages";
  }
  if (!id || !name) return null;
  const query = new URLSearchParams();
  const resourceKeys: Partial<Record<WorkspaceAppId, string[]>> = {
    missions: ["id", "view"],
    projects: ["ticket", "ticketId"],
    files: ["artifactId", "version", "mission"],
    editor: ["artifact", "draft"],
    computers: ["computer", "computerId"],
    messages: [
      "botId",
      "groupId",
      "threadId",
      "peerBotId",
      "messageId",
      "m",
      "routine",
      "routineId",
    ],
  };
  for (const key of resourceKeys[id] ?? []) {
    const value = url.searchParams.get(key);
    if (value && /^[a-zA-Z0-9_-]{1,200}$/.test(value)) query.set(key, value);
  }
  if (id === "messages" && url.searchParams.get("terminal") === "1") query.set("terminal", "1");
  if (id === "messages" && url.searchParams.get("readOnly") === "1") query.set("readOnly", "1");
  return { id, name, path: `${pathname}${query.size ? `?${query}` : ""}` };
}

export const WorkspaceSessionSchema = z.object({
  version: z.literal(1),
  paths: z.array(z.string().max(1024)).max(12),
  titles: z
    .record(z.string().max(1024), z.string().max(200))
    .refine((v) => Object.keys(v).length <= 12)
    .optional(),
});

/** Native recents preserve separate resources without importing desktop window geometry. */
export function restoreWorkspaceResources(value: unknown, platform: "web" | "mobile" = "mobile") {
  const parsed = WorkspaceSessionSchema.safeParse(value);
  if (!parsed.success) return [];
  const entries = new Map<
    string,
    NonNullable<ReturnType<typeof workspaceDestination>> & { title: string }
  >();
  for (const path of parsed.data.paths) {
    const destination = workspaceDestination(path, platform);
    if (!destination) continue;
    entries.delete(destination.path);
    entries.set(destination.path, {
      ...destination,
      title: parsed.data.titles?.[destination.path]?.trim() || destination.name,
    });
  }
  return [...entries.values()];
}

export function rememberWorkspaceResource(value: unknown, path: string, title?: string) {
  const entries = restoreWorkspaceResources(value);
  const destination = workspaceDestination(path, "mobile");
  if (!destination) return WorkspaceSessionSchema.parse(value);
  const previous = entries.find((entry) => entry.path === destination.path);
  const next = [
    ...entries.filter((entry) => entry.path !== destination.path),
    { ...destination, title: title?.trim().slice(0, 200) || previous?.title || destination.name },
  ].slice(-12);
  return {
    version: 1 as const,
    paths: next.map((entry) => entry.path),
    titles: Object.fromEntries(next.map((entry) => [entry.path, entry.title])),
  };
}

/** Deduplicate by app, keeping its most recent resource. Invalid/retired routes are ignored. */
export function restoreWorkspaceSession(value: unknown, platform: "web" | "mobile" = "web") {
  const result = WorkspaceSessionSchema.safeParse(value);
  if (!result.success) return [];
  const apps = new Map<WorkspaceAppId, NonNullable<ReturnType<typeof workspaceDestination>>>();
  for (const path of result.data.paths) {
    const destination = workspaceDestination(path, platform);
    if (destination) apps.set(destination.id, destination);
  }
  return [...apps.values()];
}

/**
 * Desktop window placement. This is device-local on purpose: the phone restores the same saved
 * resources through WorkspaceSessionSchema above and never receives desktop coordinates.
 */
export const WorkspaceBoundsSchema = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
  width: z.number().finite().positive(),
  height: z.number().finite().positive(),
});
export type WorkspaceBounds = z.infer<typeof WorkspaceBoundsSchema>;

/** A Messages window narrower than this reproduces the crushed-conversation defect. */
export const WORKSPACE_WINDOW_MIN_WIDTH = 560;
export const WORKSPACE_WINDOW_MIN_HEIGHT = 400;
/** Carried from the previous two-pane split so a shared snap edge keeps the same limits. */
export const WORKSPACE_SNAP_MIN_RATIO = 0.28;
export const WORKSPACE_SNAP_MAX_RATIO = 0.72;
const CASCADE_STEP = 28;

export const WorkspaceWindowSchema = z.object({
  id: z.string().min(1).max(100),
  path: z.string().max(1024),
  bounds: WorkspaceBoundsSchema,
  state: z.enum(["normal", "minimized", "maximized"]).default("normal"),
  /** Geometry to return to when a maximized window is restored. */
  restore: WorkspaceBoundsSchema.nullable().default(null),
  /** What this window is showing, so a put-away window can be found by name. */
  title: z.string().max(200).nullable().default(null),
});
export type WorkspaceWindow = z.infer<typeof WorkspaceWindowSchema>;

/**
 * A window's id is its app id for that app's first window, and `app#n` for further ones, so
 * records saved before windows could be told apart keep working untouched.
 */
const INSTANCE_SEPARATOR = "#";
export function workspaceWindowApp(id: string): string {
  const cut = id.indexOf(INSTANCE_SEPARATOR);
  return cut < 0 ? id : id.slice(0, cut);
}
export function nextWorkspaceWindowId(app: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  if (!used.has(app)) return app;
  for (let index = 2; ; index++) {
    const candidate = `${app}${INSTANCE_SEPARATOR}${index}`;
    if (!used.has(candidate)) return candidate;
  }
}

/** Stacking is array order: the last entry is the topmost window. */
export const WorkspaceWindowsSchema = z.object({
  version: z.literal(2),
  windows: z.array(WorkspaceWindowSchema).max(12),
  focus: z.string().max(100).nullable().default(null),
});

/** Keep every window whole and on screen so its title bar and controls stay reachable. */
export function clampWorkspaceBounds(
  bounds: WorkspaceBounds,
  desktop: WorkspaceBounds,
): WorkspaceBounds {
  const width = Math.min(Math.max(bounds.width, WORKSPACE_WINDOW_MIN_WIDTH), desktop.width);
  const height = Math.min(Math.max(bounds.height, WORKSPACE_WINDOW_MIN_HEIGHT), desktop.height);
  return {
    width,
    height,
    x: Math.min(Math.max(bounds.x, desktop.x), desktop.x + desktop.width - width),
    y: Math.min(Math.max(bounds.y, desktop.y), desktop.y + desktop.height - height),
  };
}

export function maximizedWorkspaceBounds(desktop: WorkspaceBounds): WorkspaceBounds {
  return { ...desktop };
}

export function snapWorkspaceBounds(
  side: "left" | "right",
  desktop: WorkspaceBounds,
  ratio = 0.5,
): WorkspaceBounds {
  const bounded = Math.min(WORKSPACE_SNAP_MAX_RATIO, Math.max(WORKSPACE_SNAP_MIN_RATIO, ratio));
  const leading = Math.round(desktop.width * bounded);
  const bounds =
    side === "left"
      ? { x: desktop.x, y: desktop.y, width: leading, height: desktop.height }
      : {
          x: desktop.x + leading,
          y: desktop.y,
          width: desktop.width - leading,
          height: desktop.height,
        };
  return clampWorkspaceBounds(bounds, desktop);
}

/** New windows step down the desktop instead of hiding each other exactly. */
export function cascadeWorkspaceBounds(index: number, desktop: WorkspaceBounds): WorkspaceBounds {
  const width = Math.max(WORKSPACE_WINDOW_MIN_WIDTH, Math.round(desktop.width * 0.62));
  const height = Math.max(WORKSPACE_WINDOW_MIN_HEIGHT, Math.round(desktop.height * 0.72));
  const room = Math.min(desktop.width - width, desktop.height - height);
  const steps = Math.max(1, Math.floor(room / CASCADE_STEP) + 1);
  const offset = (((index % steps) + steps) % steps) * CASCADE_STEP;
  return clampWorkspaceBounds(
    { x: desktop.x + offset, y: desktop.y + offset, width, height },
    desktop,
  );
}

/** Retired or hostile routes are dropped here, exactly as the v1 session reader drops them. */
export function restoreWorkspaceWindows(value: unknown, desktop: WorkspaceBounds) {
  const result = WorkspaceWindowsSchema.safeParse(value);
  if (!result.success) return null;
  const seen = new Set<string>();
  const windows: WorkspaceWindow[] = [];
  for (const window of result.data.windows) {
    if (seen.has(window.id) || !workspaceDestination(window.path)) continue;
    seen.add(window.id);
    // Saved bounds are the arrangement, so they are returned as they were stored. Fitting them to
    // the display happens where a window is drawn; clamping them here would let one load on a
    // small screen overwrite the arrangement for good.
    windows.push(window);
  }
  const focus = windows.some((window) => window.id === result.data.focus)
    ? result.data.focus
    : (windows.at(-1)?.id ?? null);
  return { windows, focus };
}

/**
 * Bring a saved v1 session forward. Open apps become cascaded windows in their saved order, and a
 * saved two-pane split becomes two side-by-side snapped windows at the ratio that was in use.
 */
export function migrateWorkspaceWindows(
  session: unknown,
  legacy: { split?: unknown; ratio?: unknown },
  desktop: WorkspaceBounds,
): { windows: WorkspaceWindow[]; focus: string | null } {
  const apps = restoreWorkspaceSession(session);
  const split =
    Array.isArray(legacy.split) &&
    legacy.split.length === 2 &&
    legacy.split.every((id) => typeof id === "string") &&
    legacy.split[0] !== legacy.split[1]
      ? (legacy.split as string[])
      : null;
  const saved = Number(legacy.ratio);
  const ratio =
    Number.isFinite(saved) && saved >= WORKSPACE_SNAP_MIN_RATIO && saved <= WORKSPACE_SNAP_MAX_RATIO
      ? saved
      : 0.5;
  const windows = apps.map((app, index) => {
    const side = split?.[0] === app.id ? "left" : split?.[1] === app.id ? "right" : null;
    return {
      id: app.id,
      path: app.path,
      bounds: side
        ? snapWorkspaceBounds(side, desktop, ratio)
        : cascadeWorkspaceBounds(index, desktop),
      state: "normal" as const,
      restore: null,
      title: null,
    };
  });
  return { windows, focus: windows.at(-1)?.id ?? null };
}

export const EditorDraftSchema = z.object({
  sourceArtifactId: z.string().optional(),
  version: z.literal(1),
  name: z.string().max(255),
  text: z.string().max(4 * 1024 * 1024),
  saved: z.string().max(4 * 1024 * 1024),
  preview: z.boolean().default(false),
  selectionStart: z.number().int().nonnegative().default(0),
  selectionEnd: z.number().int().nonnegative().default(0),
  scrollTop: z.number().nonnegative().default(0),
});
export type EditorDraft = z.infer<typeof EditorDraftSchema>;

/** View preferences are device-local; they never carry file contents or access grants. */
export const FilesViewSchema = z.object({
  library: LibraryFilterSchema.default({ view: "all" }),
  activeBotId: z.string().max(200).nullable().default(null),
  viewMode: z.enum(["grid", "list"]).default("list"),
  filtersOpen: z.boolean().default(false),
  searchQuery: z.string().max(200).default(""),
  dateFilter: z.enum(["all", "today", "week", "month"]).default("all"),
  scrollTop: z.number().nonnegative().max(10_000_000).default(0),
  pageCount: z.number().int().min(1).max(20).default(1),
});
export type FilesView = z.infer<typeof FilesViewSchema>;

/** Native pending files use app-private disk storage and are never submitted on restoration. */
export type ComposerDraft = z.infer<typeof ComposerDraftSchema>;

/** References use the same account/space-scoped draft storage as pending files. */
export const PendingTicketDraftSchema = z
  .array(
    TicketReferenceSchema.extend({
      id: z.string().min(1).max(500),
      threadKey: z.string().min(1).max(200),
    }),
  )
  .max(100);
export type PendingTicketReference = z.infer<typeof PendingTicketDraftSchema>[number];

export const PendingFileDraftSchema = z
  .array(
    z.object({
      id: z.string().max(500),
      threadKey: z.string().max(200),
      name: z.string().max(1000),
      mimeType: z.enum(ATTACHMENT_ALLOWED_MIME_TYPES),
      size: z.number().nonnegative(),
      contentBase64: z.string().max(70 * 1024 * 1024),
    }),
  )
  .max(ATTACHMENT_MAX_COUNT)
  .refine(
    (items) => items.reduce((sum, item) => sum + item.contentBase64.length, 0) <= 70 * 1024 * 1024,
  );

export const ComposerDraftSchema = z.object({
  text: z.string().max(1_000_000),
  mentions: z
    .array(
      z.object({
        kind: z.enum(["bot", "group", "routine", "connector", "everyone"]),
        id: z.string(),
        name: z.string(),
        subtitle: z.string().optional(),
        color: z.string().optional(),
        botId: z.string().optional(),
        connectionId: z.string().optional(),
        authStatus: z.enum(["connected", "needs_auth"]).optional(),
      }),
    )
    .max(100),
  skill: AgentSkillCatalogEntrySchema.nullable(),
});
